import {useUserSearch} from '@domain/shifthandover'
import {UserSearchAutocomplete} from '@hconnect/common/components/formFields'
import {User} from '@hconnect/common/types'
import LockIcon from '@mui/icons-material/Lock'
import {Chip, SxProps, Theme} from '@mui/material'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE} from '../../consts'

export type Props = {
  label: string
  'data-test-id': string
  errorText?: React.ReactNode
  required?: boolean
  disabled?: boolean
  onChange?: (user?: User[]) => void
  value: User[]
  lockedUsers?: User[]
  variant?: 'standard' | 'filled' | 'outlined'
  sx?: SxProps<Theme>
  plantId: string
}

export const UserSearchField: React.FC<Props> = ({
  required = false,
  onChange = () => undefined,
  value,
  disabled,
  errorText,
  label,
  'data-test-id': dataTestId,
  lockedUsers = [],
  sx,
  plantId
}: Props) => {
  const {t} = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)
  const [searchTerm, setSearchTerm] = React.useState<string | undefined>(undefined)

  const {data, isFetching} = useUserSearch({plantId, namePattern: searchTerm})

  const suggestions = useMemo(
    () =>
      ((data as User[]) ?? []).filter((user) => !value.find((assigned) => assigned.id === user.id)),
    [data, value]
  )

  return (
    <UserSearchAutocomplete
      multiple
      name="userSearch"
      value={value}
      label={label}
      required={required}
      disabled={disabled}
      forcePopupIcon={false}
      helperText={errorText}
      error={!!errorText}
      disableCloseOnSelect={false}
      sx={sx}
      placeholder={t('pleaseStartTyping')}
      data-test-id={dataTestId}
      onChange={(_, newValue, reason) => {
        if (!Array.isArray(newValue)) {
          onChange([])
          return
        }

        if (reason === 'removeOption') {
          const removed = value.find((v) => !newValue.some((nv) => nv.id === v.id))
          const isLocked = lockedUsers?.some((u) => u.id === removed?.id)
          if (isLocked) {
            return
          }
        }

        onChange(newValue)
      }}
      onInputChange={setSearchTerm}
      query={{data: suggestions, isFetching}}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const isLocked = lockedUsers?.some(({id}) => id === option.id)
          const props = getTagProps({index})
          return (
            <Chip
              {...props}
              variant="outlined"
              size="small"
              key={option.id}
              label={option.name}
              icon={isLocked ? <LockIcon sx={{fontSize: 14}} /> : undefined}
              onDelete={isLocked ? undefined : props.onDelete}
              disabled={isLocked || disabled}
              data-test-id={`${dataTestId}-tag-${option.id}`}
            />
          )
        })
      }
    />
  )
}
